import React from 'react'
import { Table, Icon, Grid, Popup } from 'semantic-ui-react'
import TimeAgo from 'react-timeago'
import filesize from 'filesize'
import { storageAPI } from '../../../utils/api'
import {
  isFolder,
  getFileExtension,
  formatLastModifiedDate,
} from '../../../utils/helpers'
import { FileObject } from '../../../utils/models'
import DeleteButton from '../DeleteButton'
import { Loader } from './Loader'
import { NoFilesPlaceholder } from './NoFilesPlaceholder'
import ShareButton from '../ShareButton'
import PrettyFileIcon from '../file-icons/PrettyFileIcon'
import { observer, useStore } from '../../../stores'
import { useTranslation } from 'react-i18next'
import { useMobile } from '../../../hooks'

const ListFiles = observer(() => {
  const { fileStore, uiStore } = useStore()
  const { t } = useTranslation()
  const isMobile = useMobile()

  const downloadFile = async (file: FileObject) => {
    const downloadURL = await storageAPI.downloadFile(file.Key!)
    window.open(downloadURL, '_blank')
  }

  const renderFolderIcon = (size: 'big' | 'huge' | 'small') => (
    <div
      style={{
        paddingLeft: '5px',
        paddingTop: '10px',
        paddingBottom: '10px',
      }}
    >
      <Icon color="yellow" size={size} name="folder open" />
    </div>
  )

  const renderRows = (files: FileObject[]) => {
    return files.map((file: FileObject) => {
      const lastModified = formatLastModifiedDate(file.LastModified!)
      const fileType = getFileExtension(file.name!)
      const isFile = !isFolder(file.name)
      const changePath = fileStore.changePath

      if (isMobile === 'isMobile') {
        return (
          <Table.Row key={file.Key}>
            <Table.Cell  className="list-layout-item">
            {isFile ? (
              <PrettyFileIcon fileType={fileType} size={40} />
            ) : (
              renderFolderIcon('big')
            )}
            </Table.Cell>
            <Table.Cell
              
              className="list-layout-item"
              onClick={() =>
                isFile ? downloadFile(file) : changePath(file.Key!)
              }
            >
              {file.name.replace('/', '')}
            </Table.Cell>
            <Table.Cell >
              {isFile && <ShareButton file={file} />}
            </Table.Cell>
            <Table.Cell >
              <DeleteButton file={file} />
            </Table.Cell>
          </Table.Row>
        )
      } else {
        return (
          <Table.Row key={file.Key}>
            <Table.Cell collapsing className="list-layout-item">
              {isFile ? (
                <PrettyFileIcon fileType={fileType} size={40} />
              ) : (
                renderFolderIcon('big')
              )}
            </Table.Cell>
            <Table.Cell
              collapsing
              className="list-layout-item"
              onClick={() =>
                isFile ? downloadFile(file) : changePath(file.Key!)
              }
            >
              {file.name.replace('/', '')}
            </Table.Cell>
            <Table.Cell collapsing>{isFile && filesize(file.Size!)}</Table.Cell>
            <Table.Cell collapsing>
              {isFile && <TimeAgo live={false} date={lastModified} />}
            </Table.Cell>
            <Table.Cell collapsing>
              {isFile && <ShareButton file={file} />}
            </Table.Cell>
            <Table.Cell collapsing>
              <DeleteButton file={file} />
            </Table.Cell>
          </Table.Row>
        )
      }
    })
  }

  // TODO: move this to its own file
  const renderBoxLayout = (files: FileObject[], foldersOnly: boolean) => {
    const filteredFiles = files.filter((file) =>
      foldersOnly ? isFolder(file.name) : !isFolder(file.name),
    )

    return (
      <>
        {/* {!!filteredFiles.length &&
          (foldersOnly ? (
            <Divider horizontal>Folders</Divider>
          ) : (
            <Divider horizontal>Files</Divider>
          ))} */}

        {filteredFiles.map((file: FileObject) => {
          const fileType = getFileExtension(file.name!)
          const isFile = !isFolder(file.name)
          const changePath = fileStore.changePath
          const fileName = file.name.replace('/', '')

          const item: React.ReactNode = (
            <div
              className="box-layout-item"
              onClick={() =>
                isFile ? downloadFile(file) : changePath(file.Key!)
              }
            >
              {isFile ? (
                <PrettyFileIcon fileType={fileType} size={60} />
              ) : (
                renderFolderIcon('huge')
              )}
              <div
                style={{
                  width: '100%',
                  // display: 'flex',
                  // flexDirection: 'row',
                  // justifyContent: 'center',
                  overflowWrap: 'break-word',
                }}
              >
                {fileName}
                {/* <TruncateString text={fileName} /> */}
              </div>
            </div>
          )

          return (
            <Grid.Column key={file.Key}>
              {fileName.length > 15 ? (
                <Popup
                  hoverable
                  trigger={item}
                  content={<>{fileName}[</>}
                  basic
                  position="bottom center"
                  style={{
                    opacity: 0.9,
                  }}
                />
              ) : (
                item
              )}
            </Grid.Column>
          )
        })}
      </>
    )
  }

  const { files, fetchingFiles } = fileStore
  const { isTableLayout, isGridLayout } = uiStore

  if (fetchingFiles) {
    return <Loader />
  }

  if (!files.length) {
    return <NoFilesPlaceholder />
  }

  if (isTableLayout) {
    // TODO: abstract this check
    if (isMobile === 'isMobile') {
      return (
        <Table compact striped selectable fixed unstackable singleLine={false} className="mobile-table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={1}/>
              <Table.HeaderCell width={3}>{t('fileName')}</Table.HeaderCell>
              <Table.HeaderCell width={1} />
              <Table.HeaderCell width={1} />
            </Table.Row>
          </Table.Header>
          <Table.Body>{renderRows(files)}</Table.Body>
        </Table>
      )
    } else {
      return (
        <Table celled striped selectable unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={1}>
                {t('type')}
              </Table.HeaderCell>
              <Table.HeaderCell>{t('fileName')}</Table.HeaderCell>
              <Table.HeaderCell width={2}>
                {t('size')}
              </Table.HeaderCell>
              <Table.HeaderCell width={1}>
                {t('lastModified')}
              </Table.HeaderCell>
              <Table.HeaderCell width={1} />
              <Table.HeaderCell width={1} />
            </Table.Row>
          </Table.Header>
          <Table.Body>{renderRows(files)}</Table.Body>
        </Table>
      )
    }
  }

  if (isGridLayout) {
    return (
      <>
        <br />
        <Grid columns={7}>{renderBoxLayout(files, true)}</Grid>
        <Grid columns={7}>{renderBoxLayout(files, false)}</Grid>
      </>
    )
  }

  return <> </>
})

export default ListFiles
